import { normalizeSelection, SelectOption } from '../../components/SalesReport'
import {
  AccessReportFilterOptions,
  AccessReportGroupBy,
  AccessReportOrigin,
  AccessReportRequest,
  CourtesyMode,
} from '../../hooks/access-report'
import {
  COURTESY_OPTIONS,
  fromWallClockMinutes,
  MAX_SELECTED_IDS,
  periodError,
  periodLabel,
  selectionLabel,
  toWallClockMinutes,
} from '../SalesReport/filters'

export { COURTESY_OPTIONS, MAX_SELECTED_IDS, periodError }

export interface AccessReportFilterState {
  periodStart: string
  periodEnd: string
  occasion: string
  sectorIds: string[]
  ticketIds: string[]
  lotIds: string[]
  deviceIds: string[]
  origins: string[]
  groupBy: AccessReportGroupBy
  courtesy: CourtesyMode
  includeCommissioners: boolean
  showPending: boolean
}

export const DEFAULT_FILTERS: AccessReportFilterState = {
  periodStart: '',
  periodEnd: '',
  occasion: '',
  sectorIds: [],
  ticketIds: [],
  lotIds: [],
  deviceIds: [],
  origins: [],
  groupBy: 'sector',
  courtesy: 'show',
  includeCommissioners: true,
  showPending: true,
}

export const UNIDENTIFIED_DEVICE = '__sem_identificacao__'
export const UNIDENTIFIED_DEVICE_LABEL = 'Sem identificação'

export const GROUP_BY_OPTIONS: Array<{ value: AccessReportGroupBy, label: string }> = [
  { value: 'sector', label: 'Setor' },
  { value: 'ticket', label: 'Ingresso' },
  { value: 'lot', label: 'Lote' },
  { value: 'origin', label: 'Origem da venda' },
  { value: 'device', label: 'Dispositivo' },
]

export const ORIGIN_LABELS: Record<AccessReportOrigin, string> = {
  web: 'Site / App',
  commissioner: 'Comissários',
  pdv: 'PDVs',
  courtesy: 'Cortesias',
}

export const ORIGIN_OPTIONS: SelectOption[] = (Object.keys(ORIGIN_LABELS) as AccessReportOrigin[]).map((origin) => ({
  value: origin,
  label: ORIGIN_LABELS[origin],
}))

const DEVICE_ID = /^([0-9a-f]{8})[0-9a-f]*-(v[\w.]+)$/i

const PLAIN_HASH = /^([0-9a-f]{8})[0-9a-f]{4,}$/i

export const deviceLabel = (deviceId: string | null): string => {
  if (deviceId === null || deviceId.trim() === '') return UNIDENTIFIED_DEVICE_LABEL
  const versioned = DEVICE_ID.exec(deviceId)
  if (versioned !== null) return `${versioned[1]} · ${versioned[2]}`
  const plain = PLAIN_HASH.exec(deviceId)
  return plain === null ? deviceId : plain[1]
}

export const groupByLabel = (groupBy: AccessReportGroupBy): string =>
  GROUP_BY_OPTIONS.find((option) => option.value === groupBy)?.label ?? groupBy

export const occasionOptions = (options: AccessReportFilterOptions): SelectOption[] =>
  options.occasions.map((occasion) => ({ value: String(occasion.id), label: occasion.name }))

export const sectorOptions = (options: AccessReportFilterOptions): SelectOption[] =>
  options.sectors.map((sector) => ({ value: String(sector.id), label: sector.name }))

const ticketsInSectors = (
  options: AccessReportFilterOptions,
  sectorIds: string[]
): AccessReportFilterOptions['tickets'] => {
  const selected = new Set(sectorIds)
  return options.tickets.filter((ticket) => selected.size === 0 || selected.has(String(ticket.sectorId)))
}

export const ticketOptions = (options: AccessReportFilterOptions, sectorIds: string[]): SelectOption[] => {
  const sectorNames = new Map(options.sectors.map((sector) => [String(sector.id), sector.name] as [string, string]))
  const visible = ticketsInSectors(options, sectorIds)
  const nameCount = visible.reduce(
    (count, ticket) => count.set(ticket.name, (count.get(ticket.name) ?? 0) + 1),
    new Map<string, number>()
  )
  return visible.map((ticket) => {
    const sectorName = sectorNames.get(String(ticket.sectorId))
    const homonym = (nameCount.get(ticket.name) ?? 0) > 1
    return { value: String(ticket.id), label: homonym && sectorName ? `${ticket.name} · ${sectorName}` : ticket.name }
  })
}

export const lotOptions = (options: AccessReportFilterOptions): SelectOption[] =>
  options.lots.map((lot) => ({ value: String(lot.id), label: lot.name }))

export const deviceOptions = (options: AccessReportFilterOptions): SelectOption[] =>
  options.devices.map((device) => ({
    value: device.id ?? UNIDENTIFIED_DEVICE,
    label: deviceLabel(device.id),
  }))

export const withSectors = (
  state: AccessReportFilterState,
  sectorIds: string[],
  options: AccessReportFilterOptions
): AccessReportFilterState => {
  const fitting = new Set(ticketsInSectors(options, sectorIds).map((ticket) => String(ticket.id)))
  return { ...state, sectorIds, ticketIds: state.ticketIds.filter((ticketId) => fitting.has(ticketId)) }
}

export const resolvedOccasion = (state: AccessReportFilterState, options: AccessReportFilterOptions): string => {
  const available = occasionOptions(options)
  if (available.some((option) => option.value === state.occasion)) return state.occasion
  return available.length > 0 ? available[0].value : '0'
}

export const withAvailableSelections = (
  state: AccessReportFilterState,
  options: AccessReportFilterOptions
): AccessReportFilterState => {
  const sectorIds = normalizeSelection(state.sectorIds, sectorOptions(options))
  return {
    ...state,
    occasion: resolvedOccasion(state, options),
    sectorIds,
    ticketIds: normalizeSelection(state.ticketIds, ticketOptions(options, sectorIds)),
    lotIds: normalizeSelection(state.lotIds, lotOptions(options)),
    deviceIds: normalizeSelection(state.deviceIds, deviceOptions(options)),
    origins: normalizeSelection(state.origins, ORIGIN_OPTIONS),
  }
}

export const activeFilterLabels = (state: AccessReportFilterState, options: AccessReportFilterOptions): string[] => {
  const applied = withAvailableSelections(state, options)
  const labels = [
    periodLabel(applied),
    selectionLabel(applied.sectorIds, sectorOptions(options), 'Setor', 'Setores'),
    selectionLabel(applied.ticketIds, ticketOptions(options, applied.sectorIds), 'Ingresso', 'Ingressos'),
    selectionLabel(applied.lotIds, lotOptions(options), 'Lote', 'Lotes'),
    selectionLabel(applied.deviceIds, deviceOptions(options), 'Dispositivo', 'Dispositivos'),
    selectionLabel(applied.origins, ORIGIN_OPTIONS, 'Origem', 'Origens'),
    applied.includeCommissioners ? null : 'Sem comissários',
  ]
  return labels.filter((label): label is string => label !== null)
}

const idList = (values: string[]): number[] | undefined => (values.length === 0 ? undefined : values.map(Number))

export const toAccessReportRequest = (state: AccessReportFilterState): AccessReportRequest => {
  const start = toWallClockMinutes(state.periodStart)
  const end = toWallClockMinutes(state.periodEnd)
  const request: AccessReportRequest = {
    occasion: Number(state.occasion === '' ? 0 : state.occasion),
    courtesy: state.courtesy,
    includeCommissioners: state.includeCommissioners,
    showPending: state.showPending,
    groupBy: state.groupBy,
  }
  if (start != null) request.periodStart = fromWallClockMinutes(start)
  if (end != null) request.periodEndExclusive = fromWallClockMinutes(end + 1)
  const sectorIds = idList(state.sectorIds)
  const ticketIds = idList(state.ticketIds)
  const lotIds = idList(state.lotIds)
  if (sectorIds) request.sectorIds = sectorIds
  if (ticketIds) request.ticketIds = ticketIds
  if (lotIds) request.lotIds = lotIds
  if (state.deviceIds.length > 0) {
    request.deviceIds = state.deviceIds.map((id) => (id === UNIDENTIFIED_DEVICE ? null : id))
  }
  if (state.origins.length > 0) request.origins = state.origins as AccessReportOrigin[]
  return request
}
